import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import DataTable from '../../components/Common/DataTable';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { 
  PlusIcon, PencilIcon, TrashIcon, EyeIcon,
  MagnifyingGlassIcon, FunnelIcon, XMarkIcon, CalendarIcon,
  PlayIcon
} from '@heroicons/react/24/outline';

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    size: 10
  });
  
  // Filter states - matches API: /api/videos/filter
  const [filters, setFilters] = useState({
    deviceId: '',
    tripId: '',
    eventId: '',
    fileId: '',
    fromDtTm: '',
    toDtTm: ''
  });
  
  const [devices, setDevices] = useState([]);
  const [trips, setTrips] = useState([]);
  const [events, setEvents] = useState([]);
  const [files, setFiles] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const { loading, get, del } = useApi();
  const navigate = useNavigate();

  // Fetch dropdown data on mount
  useEffect(() => {
    fetchDropdownData();
  }, []);

  // Fetch videos when filters or page changes
  useEffect(() => {
    fetchVideos();
  }, [filters, pageInfo.currentPage, searchTerm]);

  const fetchDropdownData = async () => {
    // Fetch devices for filter dropdown
    const devicesRes = await get('/api/devices/filter?size=100');
    if (devicesRes.data) setDevices(devicesRes.data.content || []);

    // Fetch trips for filter dropdown
    const tripsRes = await get('/api/trips/filter?size=100');
    if (tripsRes.data) setTrips(tripsRes.data.content || []);

    // Fetch events for filter dropdown
    const eventsRes = await get('/api/events/filter?size=100');
    if (eventsRes.data) setEvents(eventsRes.data.content || []);

    // Fetch files for filter dropdown
    const filesRes = await get('/api/files/filter?size=100');
    if (filesRes.data) setFiles(filesRes.data.content || []);
  };

  const fetchVideos = async () => {
    const params = new URLSearchParams();
    
    params.append('page', pageInfo.currentPage);
    params.append('size', pageInfo.size);
    
    if (filters.deviceId) params.append('deviceId', filters.deviceId);
    if (filters.tripId) params.append('tripId', filters.tripId);
    if (filters.eventId) params.append('eventId', filters.eventId);
    if (filters.fileId) params.append('fileId', filters.fileId);
    if (filters.fromDtTm) params.append('fromDtTm', filters.fromDtTm);
    if (filters.toDtTm) params.append('toDtTm', filters.toDtTm);
    
    const endpoint = `/api/videos/filter?${params.toString()}`;
    
    const { data } = await get(endpoint);
    if (data) {
      let filteredData = data.content || [];
      
      // Client-side search if searchTerm provided
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredData = filteredData.filter(video => 
          video.uuid?.toLowerCase().includes(term) ||
          video.resolution?.toLowerCase().includes(term) ||
          video.deviceId?.toString().includes(term) ||
          video.tripId?.toString().includes(term) ||
          video.eventId?.toString().includes(term)
        );
      }
      
      setVideos(filteredData);
      setPageInfo({
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        currentPage: data.number || 0,
        size: data.size || 10
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      const { error } = await del(`/api/videos/${id}`);
      if (!error) {
        fetchVideos();
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchVideos();
  };

  const clearFilters = () => {
    setFilters({
      deviceId: '',
      tripId: '',
      eventId: '',
      fileId: '',
      fromDtTm: '',
      toDtTm: ''
    });
    setSearchTerm('');
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayVideo = (video) => {
    // This would open the video player - you can implement based on your needs
    if (video.videoFileId) {
      // Navigate to video player or open in new tab
      window.open(`/api/files/${video.videoFileId}`, '_blank');
    }
  };

  const columns = [
    { header: 'ID', accessor: 'id' },
    { header: 'UUID', accessor: (row) => row.uuid ? `${row.uuid.substring(0, 8)}...` : '-' },
    { header: 'Device ID', accessor: 'deviceId' },
    { header: 'Trip ID', accessor: (row) => row.tripId || '-' },
    { header: 'Event ID', accessor: (row) => row.eventId || '-' },
    { header: 'Duration', accessor: (row) => formatDuration(row.duration) },
    { header: 'Resolution', accessor: (row) => row.resolution || '-' },
    { header: 'Framerate', accessor: (row) => row.framerate ? `${row.framerate} fps` : '-' },
    { 
      header: 'Timestamp', 
      accessor: (row) => row.timestamp ? new Date(row.timestamp).toLocaleString() : '-' 
    },
    { 
      header: 'Actions', 
      accessor: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handlePlayVideo(row)}
            className="text-green-600 hover:text-green-900"
            title="Play Video"
          >
            <PlayIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => navigate(`/videos/${row.id}`)}
            className="text-blue-600 hover:text-blue-900"
            title="View Details"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => navigate(`/videos/edit/${row.id}`)}
            className="text-indigo-600 hover:text-indigo-900"
            title="Edit"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="text-red-600 hover:text-red-900"
            title="Delete"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Videos</h1>
        <button
          onClick={() => navigate('/videos/add')}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2 w-full sm:w-auto justify-center"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Video</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by UUID, device ID, trip ID..."
              className="input-field pl-10"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Search
          </button>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg flex items-center space-x-2 ${
              showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
            <span className="hidden sm:inline">Filters</span>
          </button>
          {(filters.deviceId || filters.tripId || filters.eventId || filters.fileId || filters.fromDtTm || filters.toDtTm || searchTerm) && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
            >
              <XMarkIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Clear</span>
            </button>
          )}
        </form>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <FunnelIcon className="h-4 w-4 mr-1" />
            Filters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Device Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Device
              </label>
              <select
                name="deviceId"
                value={filters.deviceId}
                onChange={handleFilterChange}
                className="input-field text-sm"
              >
                <option value="">All Devices</option>
                {devices.map(device => (
                  <option key={device.id} value={device.id}>
                    Device #{device.id} - {device.brand} {device.devType}
                  </option>
                ))}
              </select>
            </div>

            {/* Trip Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Trip
              </label>
              <select
                name="tripId"
                value={filters.tripId}
                onChange={handleFilterChange}
                className="input-field text-sm"
              >
                <option value="">All Trips</option>
                {trips.map(trip => (
                  <option key={trip.id} value={trip.id}>
                    Trip #{trip.id} - {trip.label || 'No label'}
                  </option>
                ))}
              </select>
            </div>

            {/* Event Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Event
              </label>
              <select
                name="eventId"
                value={filters.eventId}
                onChange={handleFilterChange}
                className="input-field text-sm"
              >
                <option value="">All Events</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    Event #{event.id} - {event.eventType?.description || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>

            {/* File Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Video File
              </label>
              <select
                name="fileId"
                value={filters.fileId}
                onChange={handleFilterChange}
                className="input-field text-sm"
              >
                <option value="">All Files</option>
                {files.map(file => (
                  <option key={file.id} value={file.id}>
                    File #{file.id} - {file.path?.split('/').pop() || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>

            {/* From Date */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center">
                <CalendarIcon className="h-3 w-3 mr-1" />
                From Date
              </label>
              <input
                type="datetime-local"
                name="fromDtTm"
                value={filters.fromDtTm || ''}
                onChange={handleFilterChange}
                className="input-field text-sm"
              />
            </div>

            {/* To Date */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center">
                <CalendarIcon className="h-3 w-3 mr-1" />
                To Date
              </label>
              <input
                type="datetime-local"
                name="toDtTm"
                value={filters.toDtTm || ''}
                onChange={handleFilterChange}
                className="input-field text-sm"
              />
            </div>
          </div>

          {/* Quick Date Filters */}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 mr-2 self-center">Quick dates:</span>
            <button
              onClick={() => {
                const today = new Date();
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                setFilters(prev => ({ 
                  ...prev, 
                  fromDtTm: yesterday.toISOString().slice(0, 16),
                  toDtTm: today.toISOString().slice(0, 16)
                }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200"
            >
              Last 24 Hours
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                setFilters(prev => ({ 
                  ...prev, 
                  fromDtTm: weekAgo.toISOString().slice(0, 16),
                  toDtTm: today.toISOString().slice(0, 16)
                }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const monthAgo = new Date();
                monthAgo.setMonth(monthAgo.getMonth() - 1);
                setFilters(prev => ({ 
                  ...prev, 
                  fromDtTm: monthAgo.toISOString().slice(0, 16),
                  toDtTm: today.toISOString().slice(0, 16)
                }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200"
            >
              Last 30 Days
            </button>
          </div>
        </div>
      )}

      {/* Results count */}
      <div className="mb-4 text-sm text-gray-600">
        Showing {videos.length} of {pageInfo.totalElements} videos
      </div>

      {/* Data Table */}
      <DataTable 
        columns={columns} 
        data={videos}
        pageInfo={pageInfo}
        onPageChange={(page) => {
          setPageInfo({...pageInfo, currentPage: page});
        }}
      />
    </div>
  );
};

export default Videos;